const AVC_CODECS = [
  "avc1.640028",
  "avc1.4D4028",
  "avc1.420028",
  "avc1.640020",
  "avc1.4D4020",
  "avc1.42001F",
];

function webCodecs() {
  const g = typeof window !== "undefined" ? window : {};
  return {
    VideoEncoder: g.VideoEncoder,
    VideoFrame: g.VideoFrame,
  };
}

export function canUseWebCodecsReelEncoder() {
  const { VideoEncoder, VideoFrame } = webCodecs();
  return (
    typeof VideoEncoder === "function" &&
    typeof VideoFrame === "function" &&
    typeof VideoEncoder.isConfigSupported === "function"
  );
}

export async function pickAvcConfig(width, height, fps, bitrate, bitrateMode) {
  if (!canUseWebCodecsReelEncoder()) return null;
  const mode = bitrateMode ? { bitrateMode } : null;
  const accels = ["prefer-hardware", "no-preference", "prefer-software"];
  for (const codec of AVC_CODECS) {
    for (const hardwareAcceleration of accels) {
      const withAvc = {
        codec,
        width,
        height,
        bitrate,
        framerate: fps,
        hardwareAcceleration,
        latencyMode: "quality",
        ...(mode || {}),
        avc: { format: "avc" },
      };
      const plain = {
        codec,
        width,
        height,
        bitrate,
        framerate: fps,
        hardwareAcceleration,
        ...(mode || {}),
      };
      for (const config of [withAvc, plain]) {
        try {
          const { VideoEncoder } = webCodecs();
          const result = await VideoEncoder.isConfigSupported(config);
          if (result?.supported) return result.config || config;
        } catch {
          /* try next */
        }
      }
    }
  }
  return null;
}

/**
 * Encode painted canvas frames into a real H.264 MP4 (non-realtime).
 * Each frame gets an explicit timestamp of index / fps (60 or 120).
 * Optional reel segments + source video add AAC audio (0.5× on, 0.2× muted).
 */
export async function encodeCanvasFramesToMp4({
  canvas,
  frameCount,
  fps = 60,
  bitrate = 4_000_000,
  signal,
  paintFrame,
  onProgress,
  segments = null,
  sourceVideo = null,
}) {
  const width = canvas?.width;
  const height = canvas?.height;
  const n = Math.max(0, Math.floor(Number(frameCount) || 0));
  const rate = Number(fps) > 0 ? Number(fps) : 60;
  if (!canvas || !width || !height || n <= 0) {
    throw new Error("Nothing to encode");
  }
  const config = await pickAvcConfig(
    width,
    height,
    rate,
    Math.max(800_000, Number(bitrate) || 4_000_000)
  );
  if (!config) {
    throw new Error(`This browser cannot encode H.264 at ${rate}fps`);
  }

  let includeAudio = false;
  if (segments?.length && sourceVideo) {
    try {
      const { decodeAudioFromVideoElement } = await import("./reelAudioMux");
      const { reelExportNeedsAudio } = await import("./strokeSlowMoReel");
      if (reelExportNeedsAudio(segments)) {
        const decoded = await decodeAudioFromVideoElement(sourceVideo);
        includeAudio = Boolean(decoded);
      }
    } catch {
      includeAudio = false;
    }
  }

  const { Muxer, ArrayBufferTarget } = await import("mp4-muxer");
  const target = new ArrayBufferTarget();
  const muxer = new Muxer({
    target,
    video: {
      codec: "avc",
      width,
      height,
      frameRate: rate,
    },
    audio: includeAudio
      ? { codec: "aac", numberOfChannels: 2, sampleRate: 48000 }
      : undefined,
    fastStart: "in-memory",
    firstTimestampBehavior: "offset",
  });

  let encoderError = null;
  const { VideoEncoder, VideoFrame } = webCodecs();
  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (err) => {
      encoderError = err;
    },
  });
  encoder.configure(config);

  const frameDurationUs = Math.round(1e6 / rate);
  try {
    for (let i = 0; i < n; i += 1) {
      if (signal?.aborted) throw new Error("Export cancelled");
      if (encoderError) throw encoderError;
      await paintFrame(i);
      if (encoder.encodeQueueSize > 8) {
        await new Promise((resolve) => {
          const onDequeue = () => {
            if (encoder.encodeQueueSize <= 4) {
              encoder.removeEventListener("dequeue", onDequeue);
              resolve();
            }
          };
          encoder.addEventListener("dequeue", onDequeue);
          window.setTimeout(resolve, 40);
        });
      }
      const timestamp = i * frameDurationUs;
      const frame = new VideoFrame(canvas, {
        timestamp,
        duration: frameDurationUs,
      });
      try {
        encoder.encode(frame, { keyFrame: i % rate === 0 });
      } finally {
        frame.close();
      }
      onProgress?.({
        fraction: (i + 1) / n,
        frame: i + 1,
        totalFrames: n,
      });
    }
    await encoder.flush();
    if (includeAudio) {
      const { encodeReelAudioTrack } = await import("./reelAudioMux");
      await encodeReelAudioTrack({ muxer, segments, sourceVideo, signal });
    }
    muxer.finalize();
  } finally {
    try {
      encoder.close();
    } catch {
      /* already closed */
    }
  }
  if (encoderError) throw encoderError;
  const blob = new Blob([target.buffer], { type: "video/mp4" });
  blob._smartttExt = "mp4";
  return blob;
}
